import type { TourPlacement, TourRect } from '../types/tour.types'
import { TOUR_CONFIG } from '../config/tour.config'

export interface PopoverSize {
  width: number
  height: number
}

export interface PopoverPosition {
  top: number
  left: number
  placement: TourPlacement
  /** True when pinned to the overflow corner (no room near the target). */
  pinned: boolean
}

interface Viewport {
  width: number
  height: number
}

const ORDER: Exclude<TourPlacement, 'auto'>[] = ['bottom', 'top', 'end', 'start']

/**
 * Pure placement solver. Given the target rect and the measured popover size,
 * pick a side that keeps the popover fully on-screen and never covering the
 * target. Falls back to the top-right overflow pin when nothing fits.
 */
export function computePopoverPosition(
  target: TourRect | null,
  size: PopoverSize,
  preferred: TourPlacement,
  viewport: Viewport,
): PopoverPosition {
  const gap = TOUR_CONFIG.popoverGap
  const margin = TOUR_CONFIG.viewportMargin

  // Centered (no target) step → dead-center modal.
  if (!target) {
    return {
      top: Math.max(margin, (viewport.height - size.height) / 2),
      left: Math.max(margin, (viewport.width - size.width) / 2),
      placement: 'auto',
      pinned: false,
    }
  }

  const candidates: Exclude<TourPlacement, 'auto'>[] =
    preferred === 'auto' ? ORDER : [preferred, ...ORDER.filter((p) => p !== preferred)]

  for (const placement of candidates) {
    const pos = place(target, size, placement, gap)
    if (fits(pos, size, viewport, margin)) {
      return { ...clampCrossAxis(pos, size, viewport, margin, placement), placement, pinned: false }
    }
  }

  // Nothing fits — pin to the top-right overflow corner.
  return {
    top: TOUR_CONFIG.overflowPin.top,
    left: Math.max(margin, viewport.width - size.width - TOUR_CONFIG.overflowPin.right),
    placement: 'auto',
    pinned: true,
  }
}

function place(
  target: TourRect,
  size: PopoverSize,
  placement: Exclude<TourPlacement, 'auto'>,
  gap: number,
): { top: number; left: number } {
  const cx = target.left + target.width / 2
  const cy = target.top + target.height / 2
  switch (placement) {
    case 'bottom':
      return { top: target.top + target.height + gap, left: cx - size.width / 2 }
    case 'top':
      return { top: target.top - size.height - gap, left: cx - size.width / 2 }
    case 'end':
      return { top: cy - size.height / 2, left: target.left + target.width + gap }
    case 'start':
      return { top: cy - size.height / 2, left: target.left - size.width - gap }
  }
}

function fits(
  pos: { top: number; left: number },
  size: PopoverSize,
  vp: Viewport,
  margin: number,
): boolean {
  return (
    pos.top >= margin &&
    pos.left >= margin &&
    pos.top + size.height <= vp.height - margin &&
    pos.left + size.width <= vp.width - margin
  )
}

/** Keep the popover on-screen along the axis parallel to the target edge. */
function clampCrossAxis(
  pos: { top: number; left: number },
  size: PopoverSize,
  vp: Viewport,
  margin: number,
  placement: Exclude<TourPlacement, 'auto'>,
): { top: number; left: number } {
  const clamped = { ...pos }
  if (placement === 'top' || placement === 'bottom') {
    clamped.left = clamp(pos.left, margin, vp.width - size.width - margin)
  } else {
    clamped.top = clamp(pos.top, margin, vp.height - size.height - margin)
  }
  return clamped
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), Math.max(min, max))
}
